import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Estilos CSS para mejorar los popups de Leaflet
const mapStyles = `
  .leaflet-popup-content-wrapper {
    background: #1e293b !important;
    border-radius: 0.5rem !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5) !important;
    border: 2px solid rgba(59, 130, 246, 0.5) !important;
  }
  
  .leaflet-popup-content {
    margin: 0 !important;
    color: #f1f5f9 !important;
    font-family: system-ui, -apple-system, sans-serif !important;
  }
  
  .leaflet-popup-tip {
    background: #1e293b !important;
    border: 2px solid rgba(59, 130, 246, 0.5) !important;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
  }
  
  .leaflet-popup-close-button {
    color: #f1f5f9 !important;
    font-size: 18px !important;
    font-weight: bold !important;
    padding: 4px 8px !important;
  }
  
  .leaflet-popup-close-button:hover {
    color: #ef4444 !important;
    background: rgba(239, 68, 68, 0.1) !important;
    border-radius: 0.25rem !important;
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = mapStyles;
  document.head.appendChild(styleSheet);
}

// Componente para ajustar el zoom
const SetViewOnLoad = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
};

const GeopoliticalMap = () => {
  // Coordenadas aproximadas del Heartland (Rusia y Asia Central)
  const heartlandCoords = [
    [60, 30], [60, 100], [45, 100], [40, 60], [45, 30]
  ];

  // Coordenadas del Rimland (zonas costeras)
  const rimlandEurope = [
    [60, 0], [60, 30], [45, 30], [40, 0], [50, -10]
  ];

  const rimlandMiddleEast = [
    [40, 30], [40, 60], [25, 60], [25, 30]
  ];

  const rimlandAsia = [
    [45, 100], [50, 140], [20, 140], [20, 100]
  ];

  return (
    <div style={{ height: '500px', width: '100%', borderRadius: '1rem', overflow: 'hidden', border: '2px solid rgba(59, 130, 246, 0.3)' }}>
      <MapContainer
        center={[50, 60]}
        zoom={3}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <SetViewOnLoad center={[50, 60]} zoom={3} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        {/* Heartland */}
        <Polygon
          positions={heartlandCoords}
          pathOptions={{ 
            color: '#ef4444', 
            fillColor: '#ef4444', 
            fillOpacity: 0.3,
            weight: 3
          }}
        >
          <Popup>
            <div style={{ 
              padding: '1rem', 
              background: '#1e293b', 
              borderRadius: '0.5rem',
              border: '2px solid #ef4444',
              minWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                marginBottom: '0.75rem', 
                color: '#ef4444',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                🔴 Heartland
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '1rem',
                color: '#f1f5f9',
                fontWeight: '500',
                lineHeight: '1.4'
              }}>
                <strong>La Puerta al Heartland</strong>
              </p>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.9rem',
                color: '#cbd5e1',
                lineHeight: '1.4'
              }}>
                Polonia, Ucrania, Bielorrusia - Zona de conflicto histórico
              </p>
            </div>
          </Popup>
        </Polygon>

        {/* Rimland - Europa */}
        <Polygon
          positions={rimlandEurope}
          pathOptions={{ 
            color: '#3b82f6', 
            fillColor: '#3b82f6', 
            fillOpacity: 0.3,
            weight: 3
          }}
        >
          <Popup>
            <div style={{ 
              padding: '1rem', 
              background: '#1e293b', 
              borderRadius: '0.5rem',
              border: '2px solid #3b82f6',
              minWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                marginBottom: '0.75rem', 
                color: '#3b82f6',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                🔵 Rimland - Europa
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '1rem',
                color: '#f1f5f9',
                fontWeight: '500',
                lineHeight: '1.4'
              }}>
                <strong>Europa Occidental</strong>
              </p>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.9rem',
                color: '#cbd5e1',
                lineHeight: '1.4'
              }}>
                Francia, Alemania, Reino Unido - Zona de contención del Heartland
              </p>
            </div>
          </Popup>
        </Polygon>

        {/* Rimland - Medio Oriente */}
        <Polygon
          positions={rimlandMiddleEast}
          pathOptions={{ 
            color: '#eab308', 
            fillColor: '#eab308', 
            fillOpacity: 0.3,
            weight: 3
          }}
        >
          <Popup>
            <div style={{ 
              padding: '1rem', 
              background: '#1e293b', 
              borderRadius: '0.5rem',
              border: '2px solid #eab308',
              minWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                marginBottom: '0.75rem', 
                color: '#eab308',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                🟡 Rimland - Medio Oriente
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '1rem',
                color: '#f1f5f9',
                fontWeight: '500',
                lineHeight: '1.4'
              }}>
                <strong>Zona Energética Crítica</strong>
              </p>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.9rem',
                color: '#cbd5e1',
                lineHeight: '1.4'
              }}>
                Turquía, Irán, Golfo Pérsico - Control de recursos energéticos
              </p>
            </div>
          </Popup>
        </Polygon>

        {/* Rimland - Asia */}
        <Polygon
          positions={rimlandAsia}
          pathOptions={{ 
            color: '#8b5cf6', 
            fillColor: '#8b5cf6', 
            fillOpacity: 0.3,
            weight: 3
          }}
        >
          <Popup>
            <div style={{ 
              padding: '1rem', 
              background: '#1e293b', 
              borderRadius: '0.5rem',
              border: '2px solid #8b5cf6',
              minWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                marginBottom: '0.75rem', 
                color: '#8b5cf6',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                🟣 Rimland - Asia Oriental
              </h3>
              <p style={{ 
                margin: 0, 
                fontSize: '1rem',
                color: '#f1f5f9',
                fontWeight: '500',
                lineHeight: '1.4'
              }}>
                <strong>Centro Económico Mundial</strong>
              </p>
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.9rem',
                color: '#cbd5e1',
                lineHeight: '1.4'
              }}>
                China, Japón, Corea - Mayor densidad poblacional y económica
              </p>
            </div>
          </Popup>
        </Polygon>
      </MapContainer>
    </div>
  );
};

export default GeopoliticalMap;
